"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/nextjs";
import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { QUERIES } from "modules/common/query";
import { API_ROUTES } from "utils/api-route.constants";
import { useToast } from "modules/common/components/toast/useToast";
import { extractErrorMessage } from "utils/error-handling";
import { ServerError } from "modules/common/model/models";
import { WS_DRIVEN_CONFIG } from "modules/common/react-query-configs";
import type {
  Chat,
  CreateChatRequest,
  Message,
  SendMessageRequest,
} from "modules/chat/model/chat";

export function useChatMessages(chatId?: string) {
  const fetch = useFetchWrapper();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { userId } = useAuth();

  const messages = useQuery<Message[]>({
    queryKey: [QUERIES.MESSAGES, chatId],
    queryFn: () =>
      fetch
        .get(API_ROUTES.CHATS.MESSAGES(chatId!), null)
        .then((res: Message[]) => res),
    enabled: !!chatId,
    ...WS_DRIVEN_CONFIG,
  });

  const chats = useQuery<Chat[]>({
    queryKey: [QUERIES.CHATS],
    queryFn: () =>
      fetch
        .get(API_ROUTES.CHATS.BASE, null)
        .then((res: Chat[]) =>
          [...res].sort(
            (a, b) =>
              new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          )
        ),
    enabled: !!userId,
    ...WS_DRIVEN_CONFIG,
  });

  const getOrCreateChat = useMutation<Chat, ServerError, string[]>({
    mutationFn: (userIds: string[]) => {
      const req: CreateChatRequest = {
        userIds: userIds.filter((id) => id?.trim()),
      };
      return fetch.post(API_ROUTES.CHATS.BASE, req).then((res: Chat) => res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERIES.CHATS] });
    },
    onError: (error: ServerError) => {
      toast.error(extractErrorMessage(error, "Failed to create chat"));
    },
  });

  const sendMessage = useMutation<Message, ServerError, SendMessageRequest>({
    mutationFn: (req: SendMessageRequest) =>
      fetch
        .post(API_ROUTES.CHATS.MESSAGES(req.chatId), req)
        .then((res: Message) => res),
    onSuccess: (message) => {
      queryClient.invalidateQueries({
        queryKey: [QUERIES.MESSAGES, message.chatId],
      });
      queryClient.invalidateQueries({ queryKey: [QUERIES.CHATS] });
    },
    onError: (error: ServerError) => {
      toast.error(extractErrorMessage(error, "Failed to send message"));
    },
  });

  return {
    messages,
    chats,
    getOrCreateChat,
    sendMessage,
  };
}
